import { useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Loader2, Network as NetworkIcon, Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { MultiSelect } from '@/components/ui/multi-select'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useNetwork } from '@/contexts/NetworkContext'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import { TeamGroupsApi, type TeamGroup, type TeamGroupInput } from '@/services/users/teamGroupsApi'
import { availableTeamTypes, TeamsApi } from '@/services/users/teamsApi'
import { AppShell } from '../../components/AppShell'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { LabelAutocomplete } from '../../components/LabelAutocomplete'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { TeamMembersCard } from '../../components/TeamMembersCard'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { formatDate } from '../../utils/format'
import {
  ObjectStateToggle,
  useSaveObjectState,
  type ObjectState,
} from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

// ── Inner form component ────────────────────────────────────────────────────

interface TeamGroupEditFormProps {
  teamGroup: TeamGroup | null
  id: string
  isNew: boolean
}

function TeamGroupEditForm({ teamGroup, id, isNew }: TeamGroupEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()

  const teamTypes = useMemo(() => {
    const types = availableTeamTypes(currentNetwork)
    // Keep a saved group's type even if the network no longer offers it.
    return teamGroup?.type && !types.includes(teamGroup.type) ? [...types, teamGroup.type] : types
  }, [currentNetwork, teamGroup?.type])

  const { data: allTeams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
  })

  const form = useForm({
    defaultValues: {
      name: teamGroup?.name ?? '',
      description: teamGroup?.description ?? '',
      type: teamGroup?.type ?? (isNew ? (teamTypes[0] ?? '') : ''),
      teams: teamGroup?.teams.map((team) => team.id) ?? [],
      labels: teamGroup?.labels ?? [],
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        description: value.description,
        type: value.type || undefined,
        teams: value.teams,
        labels: value.labels,
        objectState: (teamGroup?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => TeamGroupsApi.update(id, { name: teamGroup?.name ?? '', objectState }),
    ['team-group', id],
    ['team-groups']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: TeamGroupInput) =>
      isNew ? TeamGroupsApi.create(id, input) : TeamGroupsApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['team-group', id], data)
      queryClient.invalidateQueries({ queryKey: ['team-groups'] })
      markClean()
      if (isNew) navigate(`/admin/team-groups/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew ? t('teamGroups.createdSuccess') : t('teamGroups.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('teamGroups.createFailed')
            : t('teamGroups.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  /** Teams of the group's type, plus any already in it that the list leaves out. */
  const teamOptions = (type: string) => {
    const options = allTeams
      .filter((team) => team.type === type)
      .map((team) => ({ label: team.name, value: team.id }))
    for (const team of teamGroup?.teams ?? []) {
      if (!options.some((o) => o.value === team.id))
        options.push({ label: team.name, value: team.id })
    }
    return options
  }

  const crumbs = [
    { label: t('teamGroups.title'), href: '/admin/team-groups' },
    {
      label: isNew ? t('teamGroups.newTeamGroup') : (teamGroup?.name ?? t('teamGroups.editTitle')),
    },
  ]

  return (
    <AppShell crumbs={crumbs} showBack objectType={isNew ? undefined : t('objectTypes.teamGroup')}>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={teamGroup?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.teamGroup')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('teamGroups.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={teamGroup!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {teamGroup && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={teamGroup.id} /> },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(teamGroup.createTime)}
                    updated={formatDate(teamGroup.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="ad-edit-columns">
          <div className="ad-edit-columns__info">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={NetworkIcon} />
                  {t('teamGroups.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <FieldLabel>{t('common.description')}</FieldLabel>
                    <form.Field name="description">
                      {(field) => (
                        <Textarea
                          rows={3}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('teamGroups.descriptionPlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <FieldLabel
                      tooltip={t('teamGroups.typeHelp')}
                      hint={isNew ? undefined : t('teamGroups.typeLocked')}
                    >
                      {t('common.type')}
                    </FieldLabel>
                    <form.Field name="type">
                      {(field) => (
                        <Select
                          label={t('common.type')}
                          value={field.state.value}
                          disabled={!isNew}
                          onValueChange={(v) => {
                            field.handleChange(v ?? '')
                            // The teams must all be of the group's type.
                            form.setFieldValue('teams', [])
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('teamGroups.selectType')}>
                              {field.state.value
                                ? t(`teams.types.${field.state.value}`, field.state.value)
                                : null}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {teamTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {t(`teams.types.${type}`, type)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                  <form.Subscribe selector={(s) => s.values.type}>
                    {(type) => (
                      <div className="p-field full">
                        <FieldLabel tooltip={t('teamGroups.teamsHelp')}>
                          {t('teamGroups.teams')}
                        </FieldLabel>
                        <form.Field name="teams">
                          {(field) =>
                            type ? (
                              <MultiSelect
                                options={teamOptions(type)}
                                value={field.state.value}
                                onChange={(v) => field.handleChange(v)}
                                placeholder={t('teamGroups.selectTeams')}
                              />
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                {t('teamGroups.selectTypeFirst')}
                              </p>
                            )
                          }
                        </form.Field>
                      </div>
                    )}
                  </form.Subscribe>
                  <div className="p-field full">
                    <FieldLabel>{t('common.labels')}</FieldLabel>
                    <form.Field name="labels">
                      {(field) => (
                        <LabelAutocomplete
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v)}
                        />
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {!isNew && (
            <div className="flex min-w-0 flex-1 flex-col gap-3.5">
              <TeamMembersCard kind="teamGroup" id={id} />
            </div>
          )}
        </div>

        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/team-groups')}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('teamGroups.newTeamGroup') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function TeamGroupEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: teamGroup,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['team-group', id],
    queryFn: () => TeamGroupsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('teamGroups.title'), href: '/admin/team-groups' },
    { label: isNew ? t('teamGroups.newTeamGroup') : t('teamGroups.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  return <TeamGroupEditForm key={id} teamGroup={teamGroup ?? null} id={id!} isNew={isNew} />
}
